import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const DonationSupport = () => {
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    currency: 'usd',
    isRecurring: false,
    isAnonymous: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const predefinedAmounts = [
    { value: 10, label: '$10', description: 'Buy me a coffee' },
    { value: 25, label: '$25', description: 'Support my work' },
    { value: 50, label: '$50', description: 'Generous contribution' },
    { value: 100, label: '$100', description: 'Major supporter' }
  ];

  const currencyOptions = [
    { value: 'usd', label: 'USD ($)' },
    { value: 'ngn', label: 'Nigerian Naira (₦)' },
    { value: 'xaf', label: 'Central African CFA (XAF)' },
    { value: 'zar', label: 'South African Rand (R)' }
  ];

  const supportReasons = [
    {
      icon: 'Code',
      title: 'Open Source Projects',
      description: 'Support development of free tools and libraries for the community'
    },
    {
      icon: 'GraduationCap',
      title: 'Educational Content',
      description: 'Help create tutorials, courses, and learning resources'
    },
    {
      icon: 'Users',
      title: 'Mentorship Programs',
      description: 'Enable free mentorship for aspiring developers and entrepreneurs'
    },
    {
      icon: 'Heart',
      title: 'Community Initiatives',
      description: 'Fund tech meetups, workshops, and community events'
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (value) => {
    setCustomAmount(value);
    setSelectedAmount(null);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedAmount && !customAmount) {
      newErrors.amount = 'Please select or enter an amount';
    }

    if (customAmount && (isNaN(customAmount) || parseFloat(customAmount) < 1)) {
      newErrors.amount = 'Please enter a valid amount (minimum $1)';
    }

    if (!formData?.isAnonymous) {
      if (!formData?.name?.trim()) {
        newErrors.name = 'Name is required';
      }

      if (!formData?.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
        newErrors.email = 'Valid email address is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    setTimeout(() => {
      setLoading(false);
      setSuccess(true);

      setTimeout(() => {
        setSuccess(false);
        setSelectedAmount(null);
        setCustomAmount('');
        setFormData({
          name: '',
          email: '',
          message: '',
          currency: 'usd',
          isRecurring: false,
          isAnonymous: false
        });
      }, 5000);
    }, 2000);
  };

  if (success) {
    return (
      <div className="bg-card rounded-xl p-6 md:p-8 shadow-lg border border-border">
        <div className="flex flex-col items-center justify-center text-center py-8 md:py-12">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-success/10 flex items-center justify-center mb-4 md:mb-6">
            <Icon name="Heart" size={40} color="var(--color-success)" />
          </div>
          <h3 className="text-2xl md:text-3xl font-semibold mb-3 md:mb-4">Thank You for Your Support!</h3>
          <p className="text-muted-foreground text-sm md:text-base mb-6 md:mb-8 max-w-md">
            Your generous contribution helps me continue creating valuable content, tools, and resources for the community. You'll receive a confirmation email shortly.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
            <Button
              variant="default"
              onClick={() => setSuccess(false)}
              iconName="Heart"
              iconPosition="left"
            >
              Make Another Contribution
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.href = '/home-landing'}
              iconName="Home"
              iconPosition="left"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl p-6 md:p-8 shadow-lg border border-border">
      <div className="mb-6 md:mb-8">
        <div className="flex items-center gap-3 mb-3 md:mb-4">
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br from-accent to-warning flex items-center justify-center">
            <Icon name="Heart" size={24} color="white" />
          </div>
          <div>
            <h2 className="text-2xl md:text-3xl font-semibold">Support My Work</h2>
            <p className="text-sm text-muted-foreground">Help me create more value for the community</p>
          </div>
        </div>
        <p className="text-sm md:text-base text-muted-foreground">
          Your support enables me to dedicate more time to open source projects, educational content, and community initiatives.
        </p>
      </div>
      <div className="mb-6 md:mb-8">
        <h3 className="text-lg font-semibold mb-4">What Your Support Enables</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {supportReasons?.map((reason, index) => (
            <div key={index} className="flex items-start gap-3 p-4 rounded-lg bg-muted">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name={reason?.icon} size={20} color="var(--color-primary)" />
              </div>
              <div>
                <h4 className="text-sm font-semibold mb-1">{reason?.title}</h4>
                <p className="text-xs text-muted-foreground">{reason?.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
        <div>
          <label className="block text-sm font-medium mb-3">Select Amount</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4">
            {predefinedAmounts?.map((amount) => (
              <button
                key={amount?.value}
                type="button"
                onClick={() => handleAmountSelect(amount?.value)}
                className={`p-4 rounded-lg border-2 transition-all text-center ${
                  selectedAmount === amount?.value
                    ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                }`}
              >
                <div className="text-xl md:text-2xl font-bold mb-1">{amount?.label}</div>
                <div className="text-xs text-muted-foreground">{amount?.description}</div>
              </button>
            ))}
          </div>

          <Input
            label="Or Enter Custom Amount"
            type="number"
            placeholder="Enter amount"
            value={customAmount}
            onChange={(e) => handleCustomAmountChange(e?.target?.value)}
            error={errors?.amount}
          />
        </div>

        <Select
          label="Currency"
          options={currencyOptions}
          value={formData?.currency}
          onChange={(value) => handleInputChange('currency', value)}
        />

        <div className="space-y-3">
          <Checkbox
            label="Make this a monthly contribution"
            description="Support my work with a recurring monthly donation"
            checked={formData?.isRecurring}
            onChange={(e) => handleInputChange('isRecurring', e?.target?.checked)}
          />

          <Checkbox
            label="Donate anonymously"
            description="Your name won't be displayed publicly"
            checked={formData?.isAnonymous}
            onChange={(e) => handleInputChange('isAnonymous', e?.target?.checked)}
          />
        </div>

        {!formData?.isAnonymous && (
          <div className="space-y-4">
            <Input
              label="Your Name"
              type="text"
              placeholder="Enter your name"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              error={errors?.name}
              required
            />

            <Input
              label="Email Address"
              type="email"
              placeholder="your.email@example.com"
              value={formData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              error={errors?.email}
              required
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-2">Message (Optional)</label>
          <textarea
            className="w-full min-h-[100px] px-4 py-3 rounded-lg bg-input border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            placeholder="Leave a message of support..."
            value={formData?.message}
            onChange={(e) => handleInputChange('message', e?.target?.value)}
          />
        </div>

        <Button
          type="submit"
          variant="default"
          loading={loading}
          fullWidth
          iconName="Heart"
          iconPosition="right"
        >
          {formData?.isRecurring ? 'Start Monthly Support' : 'Make Contribution'}
        </Button>
      </form>
      <div className="mt-6 md:mt-8 p-4 md:p-6 rounded-lg bg-primary/5 border border-primary/20">
        <div className="flex items-start gap-3">
          <Icon name="Shield" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-1" />
          <div>
            <h4 className="text-sm font-semibold mb-2">Secure Payment Processing</h4>
            <p className="text-xs text-muted-foreground">
              All transactions are processed securely through industry-standard payment gateways. Your financial information is never stored on our servers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonationSupport;